import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { UserX, Plus, CheckCircle2, Laptop, Calculator, AlertTriangle } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useAdminUsers } from '../hooks/useHr';
import {
  useFnfList, useFnfDetail, useInitiateFnf, useUpdateLedger, usePatchAssets, useSettleFnf,
} from '../fnf/useFnfSettlement';
import { FNF_STATUS_LABELS, type FnfSettlement, type FnfStatus } from '../fnf/types';
import { fmtDate } from '../constants';
import { localToday } from '@/shared/utils/utils';
import { formatINR } from '@/shared/utils/currency';

const STATUS_TONE: Record<FnfStatus, 'neutral' | 'warning' | 'info' | 'success'> = {
  initiated: 'neutral', pending_assets: 'warning', pending_finance: 'info', settled: 'success',
};

export function FullAndFinalPage() {
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const query = useFnfList();

  const columns: Column<FnfSettlement>[] = [
    { key: 'employee', header: 'Employee', render: (s) => <span className="font-medium text-on-surface">{s.employeeName || '—'}</span> },
    { key: 'department', header: 'Department', render: (s) => <span className="text-on-surface-variant">{s.employeeDepartment || '—'}</span> },
    { key: 'lwd', header: 'Last Working Day', render: (s) => <span className="text-on-surface-variant">{fmtDate(s.lastWorkingDay)}</span> },
    { key: 'amount', header: 'Final Amount', align: 'right', render: (s) => `${formatINR(s.finalAmount)}` },
    { key: 'status', header: 'Status', render: (s) => <Badge tone={STATUS_TONE[s.status]}>{FNF_STATUS_LABELS[s.status]}</Badge> },
  ];

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <PageHeader
        icon={UserX}
        eyebrow="People"
        title="Full &amp; Final Settlement"
        description={<>Exit clearance, asset recovery and settlement ledger</>}
        actions={
          <><Button onClick={() => setInitiateOpen(true)} size="md">
                  <Plus className="h-4 w-4" /> Initiate F&amp;F
                </Button></>
        }
      />

      <Card>
        <CardHeader title="Active Settlements" subtitle="Employees currently in the exit clearance process" />
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center p-6"><Spinner /></div>
          ) : (query.data ?? []).length === 0 ? (
            <EmptyState icon={<UserX className="h-6 w-6" />} title="No active settlements" description="Initiate an F&F settlement when an employee exits." />
          ) : (
            <Table columns={columns} data={query.data ?? []} rowKey={(s) => s._id} onRowClick={(s) => setSelectedId(s._id)} />
          )}
        </CardBody>
      </Card>

      {initiateOpen && <InitiateModal onClose={() => setInitiateOpen(false)} />}
      {selectedId && <SettlementDrawer id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

// ─── Initiate modal ───────────────────────────────────────────────────────────

function InitiateModal({ onClose }: { onClose: () => void }) {
  const { data: users = [] } = useAdminUsers();
  const initiate = useInitiateFnf();

  const [employeeUserId, setEmployeeUserId] = useState('');
  const [exitDate, setExitDate] = useState(() => localToday());
  const [lastWorkingDay, setLastWorkingDay] = useState('');
  const [reason, setReason] = useState('');
  const [noticePeriodDays, setNoticePeriodDays] = useState('0');
  const [noticePeriodServed, setNoticePeriodServed] = useState('0');

  const employeeOptions = [
    { value: '', label: 'Select employee…' },
    ...users.filter((u) => u.active).map((u) => ({ value: u.id, label: u.display_name })),
  ];

  const handleSubmit = () => {
    if (!employeeUserId || !lastWorkingDay) {
      toast.error('Employee and last working day are required.');
      return;
    }
    initiate.mutate(
      {
        employeeUserId, exitDate, lastWorkingDay, reason,
        noticePeriodDays: Number(noticePeriodDays) || 0,
        noticePeriodServed: Number(noticePeriodServed) || 0,
      },
      {
        onSuccess: () => { toast.success('F&F settlement initiated.'); onClose(); },
        onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to initiate settlement.')),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Initiate F&F Settlement"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button loading={initiate.isPending} onClick={handleSubmit}>Initiate</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Select label="Employee" value={employeeUserId} onChange={(e) => setEmployeeUserId(e.target.value)} options={employeeOptions} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Exit / Resignation Date" type="date" value={exitDate} onChange={(e) => setExitDate(e.target.value)} />
          <Input label="Last Working Day" type="date" value={lastWorkingDay} onChange={(e) => setLastWorkingDay(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Notice Period (days)" type="number" value={noticePeriodDays} onChange={(e) => setNoticePeriodDays(e.target.value)} />
          <Input label="Notice Served (days)" type="number" value={noticePeriodServed} onChange={(e) => setNoticePeriodServed(e.target.value)} />
        </div>
        <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Resignation, termination, etc." />
      </div>
    </Modal>
  );
}

// ─── Settlement detail drawer ─────────────────────────────────────────────────

function SettlementDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const query = useFnfDetail(id);
  const updateLedger = useUpdateLedger(id);
  const patchAssets = usePatchAssets(id);
  const settle = useSettleFnf(id);

  const [ledger, setLedger] = useState({
    leaveEncashmentDays: '', leaveEncashmentAmount: '', gratuityAmount: '', salaryDues: '', deductions: '',
  });

  const settlement = query.data;

  useEffect(() => {
    if (!settlement) return;
    setLedger({
      leaveEncashmentDays: String(settlement.leaveEncashmentDays),
      leaveEncashmentAmount: String(settlement.leaveEncashmentAmount),
      gratuityAmount: String(settlement.gratuityAmount),
      salaryDues: String(settlement.salaryDues),
      deductions: String(settlement.deductions),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlement?._id]);

  const saveLedger = () => {
    updateLedger.mutate(
      {
        leaveEncashmentDays: Number(ledger.leaveEncashmentDays) || 0,
        leaveEncashmentAmount: Number(ledger.leaveEncashmentAmount) || 0,
        gratuityAmount: Number(ledger.gratuityAmount) || 0,
        salaryDues: Number(ledger.salaryDues) || 0,
        deductions: Number(ledger.deductions) || 0,
      },
      {
        onSuccess: () => toast.success('Ledger updated.'),
        onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to update ledger.')),
      },
    );
  };

  const toggleAsset = (assetRecoveryId: string, recovered: boolean) => {
    patchAssets.mutate({ assetRecoveryId, recovered }, {
      onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to update asset status.')),
    });
  };

  const finalizeSettlement = () => {
    settle.mutate(undefined, {
      onSuccess: () => { toast.success('Settlement finalized.'); onClose(); },
      onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to finalize settlement.')),
    });
  };

  const assetsOutstanding = settlement?.assetRecovery.some((a) => !a.recovered) ?? false;

  return (
    <Drawer open onClose={onClose} title="F&F Settlement" width="w-[520px]">
      {query.isLoading || !settlement ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-data-lg font-semibold text-on-surface">{settlement.employeeName}</p>
              <p className="text-body-sm text-on-surface-variant">Last working day: {fmtDate(settlement.lastWorkingDay)}</p>
            </div>
            <Badge tone={STATUS_TONE[settlement.status]}>{FNF_STATUS_LABELS[settlement.status]}</Badge>
          </div>

          {settlement.assetRecovery.length > 0 && (
            <Section title="Asset Recovery">
              <div className="space-y-1.5">
                {settlement.assetRecovery.map((asset) => (
                  <label key={asset._id} className="flex items-center justify-between rounded-card-sm border border-outline bg-surface-container-low/40 px-3 py-2">
                    <span className="flex items-center gap-2 text-data-sm text-on-surface">
                      <Laptop className="h-4 w-4 text-on-surface-variant" /> {asset.label}
                    </span>
                    <input
                      type="checkbox"
                      checked={asset.recovered}
                      disabled={settlement.status === 'settled'}
                      onChange={(e) => toggleAsset(asset._id, e.target.checked)}
                      className="rounded"
                    />
                  </label>
                ))}
              </div>
            </Section>
          )}

          <Section title="Settlement Ledger">
            <div className="grid grid-cols-2 gap-2">
              <Input label="Leave Encashment Days" type="number" disabled={settlement.status === 'settled'} value={ledger.leaveEncashmentDays} onChange={(e) => setLedger({ ...ledger, leaveEncashmentDays: e.target.value })} />
              <Input label="Leave Encashment (₹)" type="number" disabled={settlement.status === 'settled'} value={ledger.leaveEncashmentAmount} onChange={(e) => setLedger({ ...ledger, leaveEncashmentAmount: e.target.value })} />
              <Input label="Gratuity (₹)" type="number" disabled={settlement.status === 'settled'} value={ledger.gratuityAmount} onChange={(e) => setLedger({ ...ledger, gratuityAmount: e.target.value })} />
              <Input label="Salary Dues (₹)" type="number" disabled={settlement.status === 'settled'} value={ledger.salaryDues} onChange={(e) => setLedger({ ...ledger, salaryDues: e.target.value })} />
              <Input label="Deductions (₹)" type="number" disabled={settlement.status === 'settled'} value={ledger.deductions} onChange={(e) => setLedger({ ...ledger, deductions: e.target.value })} className="col-span-2" />
            </div>
            {settlement.status !== 'settled' && (
              <Button variant="outline" size="sm" className="mt-2" loading={updateLedger.isPending} onClick={saveLedger}>
                <Calculator className="h-3.5 w-3.5" /> Save Ledger
              </Button>
            )}
          </Section>

          <div className="flex items-center justify-between rounded-card border border-outline bg-surface-container-low/40 px-4 py-3">
            <span className="text-data-sm font-medium text-on-surface">Final Payout</span>
            <span className="text-data-lg font-semibold text-on-surface">{formatINR(settlement.finalAmount)}</span>
          </div>

          {settlement.status !== 'settled' && (
            <div className="border-t border-outline pt-4">
              {assetsOutstanding && (
                <p className="mb-2 flex items-center gap-1.5 text-body-sm text-warning-text">
                  <AlertTriangle className="h-3.5 w-3.5" /> All assets must be recovered before settling.
                </p>
              )}
              <Button className="w-full" disabled={assetsOutstanding} loading={settle.isPending} onClick={finalizeSettlement}>
                <CheckCircle2 className="h-4 w-4" /> Finalize Settlement
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-label-caps font-semibold uppercase text-on-surface-variant">{title}</h3>
      {children}
    </div>
  );
}
